import { useEffect, useState } from "react";
import type { ChangeEvent, FormEvent, KeyboardEvent } from "react";
import type { Product } from "../types/marketplace";
import { formatCurrency, formatNumber } from "../utils/formatters";

const chevronDown = "\u25BE";
const locationStorageKey = "mika-market-location";

interface IconProps {
  className?: string;
}

function SearchIcon({ className = "" }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4.2 4.2" />
    </svg>
  );
}

function CartIcon({ className = "" }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
    >
      <path d="M3.5 5h2.1l2.1 10.2a2 2 0 0 0 2 1.6h7.5a2 2 0 0 0 1.9-1.4l1.5-5.6H7" />
      <circle cx="10" cy="20" r="1.4" />
      <circle cx="18" cy="20" r="1.4" />
    </svg>
  );
}

function LocationIcon({ className = "" }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
    >
      <path d="M12 21s6-5.3 6-11a6 6 0 0 0-12 0c0 5.7 6 11 6 11z" />
      <circle cx="12" cy="10" r="2" />
    </svg>
  );
}

function UserIcon({ className = "" }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M5 20c1.3-3.4 3.7-5 7-5s5.7 1.6 7 5" />
    </svg>
  );
}

interface HeaderProps {
  cartItemsCount?: number;
  onCartClick?: () => void;
  onHomeClick?: () => void;
  onSearchChange?: (value: string) => void;
  onProductSelect?: (product: Product) => void;
  onSearchClear?: () => void;
  searchResults?: Product[];
  searching?: boolean;
}

interface SavedLocation {
  latitude: number;
  longitude: number;
  accuracy: number;
  savedAt: string;
}

type LocationStatus = "idle" | "loading" | "saved" | "error";

function removeAccents(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function dots(value: string): string {
  return value.length > 58 ? `${value.substring(0, 58)}...` : value;
}

function readSavedLocation(): SavedLocation | null {
  if (typeof window === "undefined") {
    return null;
  }

  const storedValue = window.localStorage.getItem(locationStorageKey);

  if (!storedValue) {
    return null;
  }

  try {
    return JSON.parse(storedValue) as SavedLocation;
  } catch {
    window.localStorage.removeItem(locationStorageKey);
    return null;
  }
}

function Header({
  cartItemsCount = 0,
  onCartClick,
  onHomeClick,
  onProductSelect,
  onSearchChange,
  onSearchClear,
  searchResults = [],
  searching = false
}: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [locationStatus, setLocationStatus] = useState<LocationStatus>("idle");
  const [locationError, setLocationError] = useState<string>("");
  const [savedLocation, setSavedLocation] = useState<SavedLocation | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchFocused, setSearchFocused] = useState<boolean>(false);
  const [showLocationPrompt, setShowLocationPrompt] = useState<boolean>(false);

  useEffect(() => {
    const storedLocation = readSavedLocation();

    if (storedLocation) {
      setSavedLocation(storedLocation);
      setLocationStatus("saved");
    }

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 24);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    onSearchChange?.(removeAccents(value));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSearchChange?.(removeAccents(searchValue).trim().toLowerCase());
    setSearchFocused(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setSearchValue("");
      setSearchFocused(false);
      onSearchClear?.();
    }
  };

  const handleProductSelect = (product: Product) => {
    setSearchValue(product.name);
    setSearchFocused(false);
    onProductSelect?.(product);
  };

  const handleHomeClick = () => {
    setSearchValue("");
    setSearchFocused(false);
    onSearchClear?.();
    onHomeClick?.();
  };

  const requestBrowserLocation = () => {
    if (!("geolocation" in navigator)) {
      setLocationStatus("error");
      setLocationError("Tu navegador no permite tomar la ubicación.");
      setShowLocationPrompt(false);
      return;
    }

    setLocationStatus("loading");
    setLocationError("");
    setShowLocationPrompt(false);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nextLocation: SavedLocation = {
          accuracy: Math.round(position.coords.accuracy),
          latitude: Number(position.coords.latitude.toFixed(6)),
          longitude: Number(position.coords.longitude.toFixed(6)),
          savedAt: new Date().toISOString()
        };

        window.localStorage.setItem(locationStorageKey, JSON.stringify(nextLocation));
        setSavedLocation(nextLocation);
        setLocationStatus("saved");
      },
      (error) => {
        setLocationStatus("error");

        if (error.code === error.PERMISSION_DENIED) {
          setLocationError("Permiso denegado. Podés habilitarlo desde el navegador.");
          return;
        }

        setLocationError("No pudimos detectar tu ubicación. Intentalo de nuevo.");
      },
      {
        enableHighAccuracy: false,
        maximumAge: 1000 * 60 * 30,
        timeout: 10000
      }
    );
  };

  const handleLocationClick = () => {
    setShowLocationPrompt(true);
  };

  const hasSearch = searchValue.trim().length >= 3;
  const showSearchDropdown = searchFocused && hasSearch;
  const locationLabel =
    locationStatus === "saved" && savedLocation
      ? "Ubicación guardada"
      : locationStatus === "loading"
      ? "Detectando ubicación..."
      : "Enviar a Buenos Aires";

  return (
    <header className={`site-header ${isScrolled ? "scrolled" : ""}`}>
      <div className="topbar">
        <div className="container topbar-content">
          <div>
            <span>Bienvenido a Mika Market</span>
            <a href="#vender">Vender</a>
            <a href="#ayuda">Ayuda</a>
          </div>
          <a className="topbar-link" href="#cuenta">
            <UserIcon className="header-mini-icon" /> Mi cuenta
          </a>
        </div>
      </div>

      <div className="main-header">
        <div className="container header-grid">
          <div className="brand-area">
            <button className="brand-home" onClick={handleHomeClick} type="button">
              <span className="brand-logo" aria-hidden="true">
                <span className="brand-logo-handle" />
                <span className="brand-logo-mark">M</span>
                <span className="brand-logo-smile" />
              </span>
              <strong>Mika Market</strong>
            </button>
            <button
              className={`location-button ${locationStatus}`}
              onClick={handleLocationClick}
              type="button"
            >
              <LocationIcon className="header-mini-icon" /> {locationLabel} {chevronDown}
            </button>
            {locationError && <small className="location-error">{locationError}</small>}
          </div>

          <form className="search-box" onSubmit={handleSubmit}>
            <input
              id="market-search-input"
              onBlur={() => setSearchFocused(false)}
              onChange={handleSearchChange}
              onFocus={() => setSearchFocused(true)}
              onKeyDown={handleKeyDown}
              placeholder={"Buscar productos, marcas y m\u00e1s..."}
              value={searchValue}
            />
            <button aria-label="Buscar" type="submit">
              <SearchIcon className="header-action-icon" />
            </button>

            {showSearchDropdown && (
              <div className="search-suggestions">
                {searching && (
                  <p className="search-suggestion-status">
                    <span className="mini-spinner" /> Buscando...
                  </p>
                )}

                {!searching && searchResults.length === 0 && (
                  <p className="search-suggestion-status">No se encontraron productos</p>
                )}

                {!searching &&
                  searchResults.slice(0, 12).map((product) => (
                    <button
                      className="search-suggestion-item"
                      key={product.id ?? product.name}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleProductSelect(product)}
                      type="button"
                    >
                      <img src={product.image} alt={product.name} decoding="async" loading="lazy" />
                      <span>
                        <strong>{dots(product.name)}</strong>
                        <small>
                          {formatCurrency(product.price)}
                          {product.clientName ? ` - ${product.clientName}` : ""}
                        </small>
                      </span>
                    </button>
                  ))}
              </div>
            )}
          </form>

          <button
            className="cart-button"
            aria-label="Abrir carrito"
            onClick={onCartClick}
            type="button"
          >
            <CartIcon className="header-cart-icon" />
            <span>{formatNumber(cartItemsCount)}</span>
          </button>
        </div>
      </div>

      {showLocationPrompt && (
        <div className="location-permission-overlay" role="dialog" aria-modal="true">
          <section className="location-permission-card">
            <LocationIcon className="location-permission-icon" />
            <h2>Usar tu ubicación</h2>
            <p>
              Mika Market va a pedir permiso al navegador para guardar tu ubicación en este
              dispositivo. La usamos para estimar envíos, ordenar mejor las tiendas cercanas y
              recordar tu zona en futuras visitas.
            </p>
            <p>
              No la enviamos a servicios externos desde el navegador y podés cambiar el permiso
              cuando quieras desde la configuración del navegador.
            </p>
            <div className="location-permission-actions">
              <button onClick={requestBrowserLocation} type="button">
                Permitir ubicación
              </button>
              <button onClick={() => setShowLocationPrompt(false)} type="button">
                Ahora no
              </button>
            </div>
          </section>
        </div>
      )}
    </header>
  );
}

export default Header;
